import { api } from "@/state/services";
import router from "@/router/routes";
import native from "@/native";
import { canAccessRegionalMenu, firstRegionalUserPath } from "@/utils/regional-user-permissions";
import { limparContextoEstadual } from "@/utils/ranking-estadual";

/**
 * Sessão iniciada: espelha o token no armazenamento nativo e vincula o aparelho
 * às notificações.
 *
 * A permissão de notificação é pedida AQUI, e não na primeira abertura, de
 * propósito: pedir antes de a pessoa entender o que o aplicativo faz é a forma
 * mais rápida de levar "não" — e no iOS a pergunta só pode ser feita uma vez.
 *
 * Fora do aplicativo (navegador), as duas chamadas são no-op.
 */
function afterLogin() {
  native.session.mirror();
  native.push.register();
}

const token = localStorage.getItem("token");
const user = JSON.parse(localStorage.getItem("user"));

// onboardingReplay precisa nascer declarado aqui: no Vue 2 uma propriedade
// adicionada depois ao objeto de state nao vira reativa, e o modal de
// boas-vindas (montado em router/layouts/main.vue) observa este campo para
// reabrir o tour quando o usuario clica em "Rever tutorial".
//
// metasConfig / metasWizard (Minhas Metas): mesmo motivo — o layout main.vue é
// remontado a cada navegação, então o GET metas/config fica guardado aqui (por
// usuário) em vez de ser refeito a cada tela.
// jornadaEstado / jornadaReplay (Jornada diária): idem, GET metas/jornada por usuário+dia.
const state = token
  ? { status: { loggedIn: true }, token, user, afiliadosRegulamentoPendingPath: null, onboardingReplay: false, metasConfig: null, metasWizard: false, jornadaEstado: null, jornadaReplay: false }
  : { status: {}, token: null, user, afiliadosRegulamentoPendingPath: null, onboardingReplay: false, metasConfig: null, metasWizard: false, jornadaEstado: null, jornadaReplay: false };

const actions = {
  login({ dispatch, commit }, { personNumber, password }) {
    commit("loginRequest", { personNumber });

    api
      .post("login", { personNumber: personNumber, password: password })
      .then((response) => {
        if (response.data.status == 'success') {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.user));
          commit("loginSuccess", response.data);
          dispatch("alert/success", response.data.message, { root: true });
          afterLogin();

          if (response.data.user && response.data.user.documents && response.data.user.documents == 'pending') {
            router.push("/documents");
          } else {
            router.push("/");
          }
        } else {
          commit("loginFailure", response.data.message);
          dispatch("alert/error", response.data.message, { root: true });
        }
      });
  },
  regionalUserLogin({ dispatch, commit }, { personNumber, password }) {
    commit("loginRequest", { personNumber });

    api
      .post("login/regional-user", { personNumber: personNumber, password: password })
      .then((response) => {
        if (response.data.status == 'success') {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.user));
          commit("loginSuccess", response.data);
          dispatch("alert/success", response.data.message, { root: true });
          afterLogin();

          const path = canAccessRegionalMenu(response.data.user, "/")
            ? "/"
            : firstRegionalUserPath(response.data.user);
          router.push(path);
        } else {
          commit("loginFailure", response.data.message);
          dispatch("alert/error", response.data.message, { root: true });
        }
      })
      .catch((error) => {
        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Nao foi possivel autenticar o acesso de secretaria.";

        commit("loginFailure", message);
        dispatch("alert/error", message, { root: true });
      });
  },
  token({ dispatch, commit }, { key }) {
    api.post("login/token", { token: key }).then((response) => {
      if (response.data.status == 'success') {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        commit("loginSuccess", response.data);
        dispatch("alert/success", response.data.message, { root: true });
        afterLogin();
        router.push("/");
      } else {
        commit("loginFailure", response.data.message);
        dispatch("alert/error", response.data.message, { root: true });
      }
    });
  },
  avatar({ dispatch, commit }) {
    api.get("user").then((response) => {
      if (response.data.status == 'success') {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        commit("avatar", response.data.user);
        dispatch("alert/success", response.data.message, { root: true });
      }
    });
  },
  logout({ commit }) {
    // Desvincula o aparelho ANTES de apagar o token: o endpoint de
    // desregistro é autenticado e precisa do JWT que está saindo. Sem isso, o
    // aparelho continuaria recebendo notificação do usuário anterior.
    native.push.unregister();

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    limparContextoEstadual();

    native.session.clear();

    commit("logout");
  },
};

const mutations = {
  loginRequest(state, token) {
    state.status = { loggingIn: true };
    state.token = token;
  },
  loginSuccess(state, data) {
    // Sessão nova: a diretoria do Ranking Estadual é recarregada (o menu pede de novo).
    if (data && data.token !== state.token) limparContextoEstadual();
    state.status = { loggedIn: true };
    state.token = data.token;
    state.user = JSON.parse(JSON.stringify(data.user));
  },
  loginFailure(state) {
    state.status = {};
    state.token = null;
  },
  logout(state) {
    state.status = {};
    state.token = null;
  },
  avatar(state, user) {
    state.user = JSON.parse(JSON.stringify(user));
  },
  // Onboarding (tour de boas-vindas). Mutations dedicadas em vez de mexer
  // direto em state.account.* de dentro do componente.
  setOnboardingReplay(state, value) {
    state.onboardingReplay = !!value;
  },
  setOnboardingDone(state) {
    if (!state.user) return;
    state.user = { ...state.user, onboarding_done: "yes" };
    localStorage.setItem("user", JSON.stringify(state.user));
  },
  // Minhas Metas: cache do GET metas/config ({ key, data }) e pedido de abertura
  // do wizard de planejamento ("Refazer meu planejamento").
  setMetasConfig(state, value) {
    state.metasConfig = value || null;
  },
  setMetasWizard(state, value) {
    state.metasWizard = !!value;
  },
  // Jornada diária: cache do GET metas/jornada ({ key, dia, data }) e pedido de
  // "Rever jornada agora" (botão em Minhas Metas).
  setJornadaEstado(state, value) {
    state.jornadaEstado = value || null;
  },
  setJornadaReplay(state, value) {
    state.jornadaReplay = !!value;
  },
  registerRequest(state) {
    state.status = { registering: true };
  },
  registerSuccess(state) {
    state.status = {};
  },
  registerFailure(state) {
    state.status = {};
  },
};

export const account = {
  namespaced: true,
  state,
  actions,
  mutations,
};
